import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, BarChart3, TrendingUp, CheckCircle2, Award, Tag,
  MessageCircle, Target, Smartphone, Monitor, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BrandLogo } from '../components/BrandLogo';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { startCheckout } from '../lib/checkout';

const loopSteps = [
  { n: '01', label: 'Play',     desc: 'Play the game. Create the moments. Compete.' },
  { n: '02', label: 'Feedback', desc: 'Capture meaningful moments and connect them to specific areas of development.' },
  { n: '03', label: 'Improve',  desc: 'Understand the feedback and turn it into an actionable adjustment.' },
  { n: '04', label: 'Grow',     desc: 'Track progress over time and see how small improvements become lasting development.' },
];

const features = [
  { icon: BarChart3, title: '10-Category Rubric', desc: 'Evaluate the complete player — not just the puck. From skating and individual skills to hockey sense, discipline and work ethic.' },
  { icon: Tag, title: 'Video Moment Tags', desc: 'Turn video into actionable feedback. Tag specific moments, connect them to a development category, and give the player context they can understand.' },
  { icon: TrendingUp, title: 'Progress Tracking', desc: 'See development over time. Identify trends, measure improvement and know where the next opportunity lies.' },
  { icon: MessageCircle, title: 'Player & Family View', desc: 'Give players and families a private window into development — complete with tagged clips, coach notes and progress.' },
  { icon: Target, title: 'Position-Aware Feedback', desc: 'Forwards, defense and goalies receive development prompts designed around the demands of their position.' },
  { icon: Award, title: 'Reports That Tell the Story', desc: 'Create professional reports for player meetings, parent conversations, recruiting and next-level opportunities.' },
];

const categoryList = [
  { name: 'Skating Elements', score: 7.7 },
  { name: 'Offensive Individual Skills', score: 8.1 },
  { name: 'Defensive Individual Skills', score: 6.6 },
  { name: 'Plays With Speed', score: 8.3 },
  { name: 'Offensive Team Play', score: 7.3 },
  { name: 'Defensive Team Play', score: 6.8 },
  { name: 'Hockey Sense', score: 7.3 },
  { name: 'Strength / Power', score: 8.5 },
  { name: 'Discipline', score: 6.5 },
  { name: 'Work Ethic', score: 7.7 },
];

const tiers = [
  {
    name: 'Player / Parent', priceMonthly: 9, priceAnnual: 90, saveAnnual: 18,
    desc: 'For athletes and families who want to own the development process with self-assessments and video review.',
    features: ['Athlete self-assessments', 'Personal development profile', 'Access to coach feedback', 'Tagged video moments', 'Progress tracking', 'Evaluation history', 'Private player/family view'],
    cta: 'Get Started',
  },
  {
    name: 'Coach / Advisor', priceMonthly: 19, priceAnnual: 190, saveAnnual: 38,
    desc: 'For coaches, advisors and development professionals working directly with players.',
    features: ['Up to 25 athletes', 'Unlimited evaluations', 'Video moment tags', '10-category rubric', 'Progress tracking', 'Player/parent access', 'CSV & printable reports'],
    cta: 'Get Started',
  },
  {
    name: 'Team / Camp', priceMonthly: 29, priceAnnual: 290, saveAnnual: 58, tierMonth: 'team_month', tierYear: 'team_year',
    desc: 'For teams, camps and group development programs.',
    features: ['Up to 50 athletes', 'Multiple coaches', 'Unlimited evaluations', 'Video moment tags', 'Player & family access', 'Team development tracking', 'Reports & evaluations', 'Priority email support'],
    cta: 'Get Started', highlighted: true,
  },
  {
    name: 'Academy', priceMonthly: null, priceAnnual: null,
    desc: 'For multi-team programs, academies and organizations.',
    features: ['Unlimited coaches', 'Unlimited athletes', 'Multiple teams & programs', 'Custom onboarding', 'Organization-wide tracking', 'Advanced reporting', 'Dedicated support'],
    cta: 'Talk to Us',
  },
];

// Simple hand-drawn line-art icons matching the poster style
const LineIcon = ({ children }) => (
  <svg viewBox="0 0 64 64" width="56" height="56" fill="none" stroke="#1e40af" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const WhistleSVG = () => (
  <LineIcon>
    <path d="M8 30 L30 30 L38 22 L52 22 C56 22 58 25 58 29 L58 35 C58 39 56 42 52 42 L38 42 L30 34 L8 34 Z" />
    <circle cx="18" cy="32" r="3.5" fill="#1e40af" stroke="none" />
    <path d="M52 22 L54 14" />
  </LineIcon>
);
const BinocularsSVG = () => (
  <LineIcon>
    <rect x="6"  y="18" width="20" height="30" rx="6" />
    <rect x="38" y="18" width="20" height="30" rx="6" />
    <path d="M26 26 L38 26" />
    <path d="M26 34 L38 34" />
    <path d="M14 14 L18 22 M46 14 L50 22" />
    <circle cx="16" cy="34" r="4" />
    <circle cx="48" cy="34" r="4" />
  </LineIcon>
);
const ClipboardSVG = () => (
  <LineIcon>
    <rect x="14" y="12" width="36" height="44" rx="4" />
    <rect x="24" y="8"  width="16" height="10" rx="2" fill="#1e40af" stroke="none" />
    <path d="M22 26 L28 30 L38 22" />
    <path d="M42 34 L28 46" />
    <circle cx="42" cy="34" r="2.5" fill="#1e40af" stroke="none" />
    <circle cx="28" cy="46" r="2.5" fill="#1e40af" stroke="none" />
  </LineIcon>
);
// Athlete/skater silhouette — self-assessment persona
const AthleteSVG = () => (
  <LineIcon>
    {/* head */}
    <circle cx="34" cy="14" r="6" />
    {/* body / stick */}
    <path d="M34 20 L30 32 L38 44 L34 56" />
    <path d="M30 32 L18 36" />
    <path d="M18 36 L10 54" />
    {/* trailing hockey stick */}
    <path d="M38 44 L54 40 L58 46" />
    {/* skate line */}
    <path d="M28 58 L42 58" />
  </LineIcon>
);

// The mocked-up phone assessment card (right column of hero)
function PhoneMock() {
  return (
    <div className="relative mx-auto" style={{ width: 320 }}>
      {/* device frame */}
      <div className="relative rounded-[38px] p-[10px] bg-slate-900 shadow-[0_35px_60px_-15px_rgba(11,31,77,0.45)]">
        <div className="rounded-[30px] bg-white overflow-hidden border border-slate-200">
          {/* status bar */}
          <div className="bg-[#0b1f4d] px-4 py-3 flex items-center justify-between">
            <span className="text-white/80 text-xs">☰</span>
            <div className="text-white font-brand tracking-widest text-sm">
              SHIFTNOTES<sup className="text-[8px] text-white/70 ml-0.5">™</sup>
            </div>
            <span className="text-white/80 text-xs">◉</span>
          </div>

          <div className="px-5 py-5">
            <div className="text-[9px] tracking-[0.35em] uppercase text-slate-400 mb-0.5">Player</div>
            <h3 className="font-brand text-2xl text-[#0b1f4d] leading-none">PLAYER ASSESSMENT</h3>
            <div className="text-xs text-slate-500 mt-1">10 Key Development Categories</div>

            <ul className="mt-4 space-y-3">
              {categoryList.map((c, i) => (
                <li key={c.name}>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-800 font-semibold truncate pr-2">{c.name}</span>
                    <span className="font-mono text-[#1e40af] font-bold">{c.score.toFixed(1)}</span>
                  </div>
                  <div className="h-1.5 mt-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#1e40af] to-[#1d4ed8] rounded-full"
                      style={{ width: `${(c.score / 10) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const buyTier = (tierKey) => startCheckout(tierKey, { user, navigate, toast });

  const [teaserEmail, setTeaserEmail] = useState('');
  const [teaserSubmitting, setTeaserSubmitting] = useState(false);
  const handleTeaserSubmit = async (e) => {
    e.preventDefault();
    if (!teaserEmail) return;
    setTeaserSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        email: teaserEmail,
        source: 'lacrosse_soccer_teaser',
      });
      toast({ title: "You're on the list!", description: "We'll email you the moment lacrosse and soccer launch." });
      setTeaserEmail('');
    } catch (err) {
      toast({ title: 'Could not add you', description: err?.response?.data?.detail || 'Try again in a moment.' });
    } finally {
      setTeaserSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity" data-testid="nav-brand">
            <BrandLogo variant="inline" />
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/pricing')} className="hidden md:inline-flex text-sm text-slate-600 hover:text-[#0b1f4d]">Pricing</button>
            <Button variant="ghost" className="text-slate-600 hover:text-[#0b1f4d]" onClick={() => navigate('/login')}>Sign in</Button>
            <Button className="bg-[#1e40af] hover:bg-[#1d4ed8] text-white font-semibold" onClick={() => navigate('/pricing')} data-testid="nav-get-started">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* HERO - matches the reference poster */}
      <section className="relative overflow-hidden rink-bg">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10 lg:pt-24 lg:pb-16 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          {/* LEFT */}
          <div className="animate-fade-in-up">
            <BrandLogo variant="full" className="mb-8" />

            <div className="uppercase tracking-[0.28em] text-xs font-semibold text-[#0b1f4d]/80 mb-3">
              Just in time for
            </div>

            <h1
              className="font-brand headline-etched leading-[0.92] uppercase text-[44px] sm:text-[64px] lg:text-[84px] xl:text-[92px] whitespace-nowrap"
              data-testid="hero-headline"
            >
              <span className="brush-underline">Hockey Season.</span>
            </h1>

            <p className="mt-8 text-lg text-slate-700 max-w-xl leading-relaxed">
              <span className="text-slate-900 font-semibold">The game moves fast.</span>{' '}
              <span className="text-[#1e40af] font-semibold">Player development shouldn't stop</span>{' '}
              when the whistle blows.
            </p>

            <p className="mt-5 text-base text-slate-700 max-w-xl leading-relaxed">
              <span className="text-[#1e40af] font-semibold">ShiftNotes</span>
              <sup className="text-[10px] text-[#1e40af]">™</sup> gives coaches, scouts, advisors —{' '}
              <span className="text-[#1e40af] font-semibold">and the athletes themselves</span> — a simple way to evaluate, self-assess and accelerate player development through{' '}
              <span className="text-[#1e40af] font-semibold">detailed assessments and video-based feedback.</span>
            </p>

            {/* Persona row */}
            <div className="mt-8 grid grid-cols-4 max-w-lg gap-4">
              {[
                { svg: <WhistleSVG />,     label: 'Coaches' },
                { svg: <BinocularsSVG />,  label: 'Scouts' },
                { svg: <ClipboardSVG />,   label: 'Advisors' },
                { svg: <AthleteSVG />,     label: 'Athletes' },
              ].map((p) => (
                <div key={p.label} className="flex flex-col items-center text-center">
                  {p.svg}
                  <div className="mt-2 font-brand text-sm tracking-[0.22em] text-[#0b1f4d]">
                    {p.label.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>

            {/* Painted brush stripe callout */}
            <div className="relative mt-8 max-w-md">
              <div className="brush-stripe px-6 py-4 pr-8 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/95 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-[#0b1f4d]" strokeWidth={2.5} />
                </div>
                <div className="font-brand text-white uppercase tracking-[0.18em] text-lg leading-tight">
                  Also a powerful<br />self-assessment tool for athletes
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate('/pricing')} className="bg-[#1e40af] hover:bg-[#1d4ed8] text-white font-semibold" data-testid="hero-get-started">
                Get Started Today <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/pricing')}
                className="border-[#0b1f4d]/30 text-[#0b1f4d] hover:bg-slate-100 bg-transparent">
                See Pricing
              </Button>
            </div>
          </div>

          {/* RIGHT - phone mock */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="absolute -inset-8 bg-gradient-to-br from-[#1e40af]/15 via-[#0b1f4d]/10 to-transparent rounded-[3rem] blur-3xl" />
            <div className="relative">
              <PhoneMock />
            </div>
          </div>
        </div>

        {/* Bottom info strip - matches getshiftnotes.com bar in poster */}
        <div className="relative bg-[#1e40af]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4 text-white">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center">
                <Globe className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/75">Get started today</div>
                <a href="/" className="font-brand text-xl tracking-wide">getshiftnotes.com</a>
              </div>
            </div>
            <div className="h-8 w-px bg-white/30 hidden md:block" />
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Smartphone className="h-6 w-6 text-white" strokeWidth={2} />
                <Monitor className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/75">Available on</div>
                <div className="font-brand text-sm tracking-wide">Mobile &amp; Web</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process section */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-semibold text-[#1e40af] uppercase tracking-[0.28em] mb-3">The ShiftNotes™ Development Process</div>
            <h2 className="font-brand text-4xl md:text-5xl uppercase text-[#0b1f4d] leading-tight">Turn Every Shift Into an Opportunity to Grow.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {loopSteps.map(s => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#1e40af]/40 transition-colors">
                <div className="font-mono text-sm text-[#1e40af] mb-2">{s.n}</div>
                <h3 className="font-brand text-xl uppercase text-[#0b1f4d] mb-2">{s.label}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Athletes / self-assessment spotlight */}
      <section className="bg-white" data-testid="self-assessment-section">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="text-xs font-semibold text-[#1e40af] uppercase tracking-[0.28em] mb-3">Built for athletes too</div>
            <h2 className="font-brand text-4xl md:text-5xl uppercase text-[#0b1f4d] leading-tight">
              The best players scout <span className="text-[#1e40af]">themselves.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              ShiftNotes<sup className="text-[10px] text-[#1e40af]">™</sup> isn't just for coaches. Athletes get their own view of the 10-category rubric so they can rate their own game, compare it to their coach's evaluation, and see the exact gap between how they see themselves and how they're seen on the ice.
            </p>
            <ul className="mt-6 space-y-3 text-slate-700">
              {[
                'Score yourself across the same 10 development categories the pros use.',
                'Tag your own video — the moments you feel proudest of, and the ones you want to fix.',
                'Compare self-assessment to coach evaluation side-by-side.',
                'Track your development curve week over week, season over season.',
              ].map(line => (
                <li key={line} className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#1e40af] mt-0.5 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1e40af]/10 border border-[#1e40af]/20 px-4 py-2 text-[#1e40af] font-semibold text-sm">
              Ownership becomes acceleration.
            </div>
          </div>

          {/* Right column: split-card mock — Coach eval vs Athlete self-assessment */}
          <div className="order-1 lg:order-2 relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#1e40af]/10 via-[#0b1f4d]/5 to-transparent rounded-[2.5rem] blur-2xl" />
            <div className="relative grid grid-cols-2 gap-3">
              {[
                { who: 'Coach', tone: 'bg-[#0b1f4d] text-white',       accent: 'bg-white/25' },
                { who: 'You',   tone: 'bg-white text-[#0b1f4d] border border-slate-200', accent: 'bg-[#1e40af]' },
              ].map(card => (
                <div key={card.who} className={`rounded-2xl p-5 shadow-sm ${card.tone}`}>
                  <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">{card.who === 'You' ? 'Self assessment' : 'Coach eval'}</div>
                  <div className="font-brand text-2xl mt-1">{card.who.toUpperCase()}</div>
                  <ul className="mt-4 space-y-2.5">
                    {categoryList.slice(0, 6).map((c, i) => {
                      const val = card.who === 'You' ? Math.max(3, c.score - 0.7 - (i % 2) * 0.4) : c.score;
                      return (
                        <li key={c.name}>
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="truncate pr-1">{c.name}</span>
                            <span className="font-mono font-bold">{val.toFixed(1)}</span>
                          </div>
                          <div className={`h-1.5 mt-0.5 rounded-full ${card.who === 'You' ? 'bg-slate-100' : 'bg-white/15'} overflow-hidden`}>
                            <div className={`h-full ${card.accent} rounded-full`} style={{ width: `${(val / 10) * 100}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <div className="relative mt-3 text-center text-xs text-slate-500">
              Side-by-side clarity — the gap between how you see it and how the coach sees it.
            </div>
          </div>
        </div>
      </section>

      {/* Platform / Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl mb-14">
          <div className="text-xs font-semibold text-[#1e40af] uppercase tracking-[0.28em] mb-3">The Platform</div>
          <h2 className="font-brand text-4xl md:text-5xl uppercase text-[#0b1f4d] leading-tight">Turn Feedback Into Development.</h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Great coaching isn't just about identifying what happened. It's about helping the player understand why it happened, what to do differently, and whether they're improving.
          </p>
          <p className="mt-3 text-slate-600 text-lg leading-relaxed">
            ShiftNotes connects video, coach feedback, player development and progress tracking in one simple process.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map(f => (
            <div key={f.title} className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-[#1e40af]/40 hover:shadow-md transition-all">
              <div className="h-11 w-11 rounded-lg bg-[#1e40af]/10 border border-[#1e40af]/20 flex items-center justify-center mb-4 group-hover:bg-[#1e40af]/20 transition-colors">
                <f.icon className="h-5 w-5 text-[#1e40af]" />
              </div>
              <h3 className="font-brand text-lg uppercase text-[#0b1f4d] mb-2 tracking-wide">{f.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Framework */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-semibold text-[#1e40af] uppercase tracking-[0.28em] mb-3">The Framework</div>
              <h2 className="font-brand text-4xl md:text-5xl uppercase text-[#0b1f4d] leading-tight">10 Categories. 80+ Criteria. One Clear Picture.</h2>
              <p className="mt-5 text-slate-600 text-lg leading-relaxed">
                Every evaluation walks a coach through a comprehensive technical, tactical and character-based development framework.
              </p>
              <p className="mt-4 text-[#0b1f4d] font-semibold">Evaluate the whole player — not just the player with the puck.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {categoryList.map((c, i) => (
                <div key={c.name} className="flex items-center gap-2.5 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm">
                  <span className="font-mono text-xs text-[#1e40af]">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-slate-700">{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs font-semibold text-[#1e40af] uppercase tracking-[0.28em] mb-3">Built for Every Step of the Journey</div>
          <h2 className="font-brand text-4xl md:text-5xl uppercase text-[#0b1f4d] leading-tight">Player, Coach, Team or Academy — ShiftNotes Fits Your Game.</h2>
          <p className="mt-4 text-slate-600 text-lg">Simple, affordable pricing designed to make player development accessible to everyone.</p>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {tiers.map(t => (
            <div key={t.name} className={`relative rounded-2xl p-7 border bg-white ${t.highlighted ? 'border-[#1e40af] shadow-lg ring-1 ring-[#1e40af]/20' : 'border-slate-200'}`}>
              {t.highlighted && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1e40af] text-white border-[#1e40af] font-semibold">Most Popular</Badge>}
              <div className="font-brand text-lg uppercase text-[#0b1f4d] tracking-wide">{t.name}</div>
              <p className="text-sm text-slate-600 mt-2 min-h-[60px]">{t.desc}</p>
              <div className="mt-4">
                {t.priceMonthly ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="font-brand text-4xl text-[#0b1f4d]">${t.priceMonthly}</span>
                      <span className="text-slate-500">/ month</span>
                    </div>
                    <div className="text-sm text-slate-500 mt-1">or ${t.priceAnnual} / year</div>
                  </>
                ) : (
                  <div className="font-brand text-3xl text-[#0b1f4d]">Custom Pricing</div>
                )}
              </div>
              <ul className="mt-5 space-y-2 text-sm min-h-[190px]">
                {t.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-[#1e40af] mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => t.priceMonthly ? buyTier(t.tierYear) : (window.location.href = 'mailto:rob@shiftnotes.io?subject=ShiftNotes%20Academy%20Inquiry')}
                className={`w-full mt-6 font-semibold ${t.highlighted ? 'bg-[#1e40af] hover:bg-[#1d4ed8] text-white' : 'bg-[#0b1f4d] hover:bg-[#0b1f4d]/90 text-white'}`}>
                {t.cta}
              </Button>
              {t.saveAnnual && <div className="text-xs text-emerald-700 text-center mt-3">Save ${t.saveAnnual} with annual billing.</div>}
            </div>
          ))}
        </div>
      </section>

      {/* About the Coach */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-[380px_1fr] gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#1e40af]/15 via-[#0b1f4d]/10 to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200 shadow-lg">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_player-dev-hub-13/artifacts/yk23036i_Rob%20Morgan%20photo.jpeg"
                alt="Rob Morgan — Founder, ShiftNotes"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-4 text-center">
              <div className="font-brand text-lg uppercase text-[#0b1f4d] tracking-wide">Rob Morgan</div>
              <div className="text-sm text-slate-500">Founder — NextShift Development LLC</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-[#1e40af] uppercase tracking-[0.28em] mb-3">About the Coach</div>
            <h2 className="font-brand text-4xl md:text-5xl uppercase text-[#0b1f4d] leading-tight">Built by a Coach. Designed for Development.</h2>
            <div className="mt-6 space-y-4 text-slate-600 text-lg leading-relaxed">
              <p>
                Rob Morgan brings more than 25 years of coaching experience at the NCAA and professional levels to ShiftNotes<sup className="text-[10px] text-[#1e40af]">™</sup>. Throughout his career, he has worked with hundreds of student-athletes, helping them understand that development isn't just about identifying what needs to improve — it's about creating a process to get better.
              </p>
              <p>
                As the founder and creator of ShiftNotes<sup className="text-[10px] text-[#1e40af]">™</sup>, Rob built the platform to connect what happens on the ice with what happens next — giving coaches a simple way to capture meaningful moments, provide actionable feedback, and help players track their growth.
              </p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-[#1e40af] font-semibold">
              Be The Best at Getting Better Each Day.
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="relative overflow-hidden bg-[#0b1f4d] rounded-2xl p-10 md:p-14 text-center">
          <div className="absolute inset-0 hero-grid opacity-15" />
          <div className="relative">
            <BrandLogo variant="stack" tone="light" className="mx-auto mb-6" />
            <h2 className="font-brand text-3xl md:text-4xl uppercase text-white">Ready to Unlock Your Next Shift?</h2>
            <p className="text-white/80 mt-3 max-w-xl mx-auto">Start building a better development process today.</p>
            <Button size="lg" onClick={() => navigate('/pricing')} className="mt-7 bg-white hover:bg-slate-100 text-[#0b1f4d] font-semibold">
              Get Started <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Sport rollout teaser */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="sport-teaser">
        <div className="relative bg-white border-2 border-dashed border-[#1e40af]/40 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0 h-14 w-14 rounded-full bg-[#1e40af] text-white flex items-center justify-center font-brand text-lg tracking-widest">
            SOON
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="text-xs uppercase tracking-[0.28em] text-[#1e40af] font-semibold mb-1">Sport rollout</div>
            <h3 className="font-brand text-2xl md:text-3xl uppercase text-[#0b1f4d] leading-tight">
              Lacrosse and Soccer are in development.
            </h3>
            <p className="text-slate-600 mt-2 text-sm md:text-base">
              Same development process, purpose-built for each sport. Drop your email and we'll let you know the moment it's ready.
            </p>
          </div>
          <form onSubmit={handleTeaserSubmit} className="flex gap-2 w-full md:w-auto md:min-w-[360px]">
            <input
              type="email"
              required
              placeholder="you@team.com"
              value={teaserEmail}
              onChange={e => setTeaserEmail(e.target.value)}
              data-testid="sport-teaser-email"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e40af]/40 bg-white text-slate-900"
            />
            <Button type="submit" disabled={teaserSubmitting}
              data-testid="sport-teaser-submit"
              className="bg-[#1e40af] hover:bg-[#1d4ed8] text-white font-semibold">
              {teaserSubmitting ? 'Adding' : 'Notify me'}
            </Button>
          </form>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div>ShiftNotes<sup className="text-[9px] text-[#1e40af]">™</sup> — a product of NextShift Development LLC</div>
          <div>© {new Date().getFullYear()} NextShift Development LLC. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
